import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { History, Plus, GripVertical, Check, Trash2, Pencil } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { format } from 'date-fns';

import { supabase } from '@/integrations/supabase/client';
import { useAdfChangelog, AdfChangelogEntry } from '@/hooks/useAdfChangelog';
import { useToast } from '@/hooks/use-toast';
import { AdminPageHeader, AdminSheet, AdminTableSkeleton, AdminEmptyState, DeleteConfirmDialog } from './shared';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { SearchInput } from '@/components/shared/SearchInput';

interface FormData {
  version: string;
  release_date: string;
  changes: string[];
  is_current: boolean;
}

const defaultFormData: FormData = {
  version: '',
  release_date: new Date().toISOString().split('T')[0],
  changes: [''],
  is_current: false,
};

export default function AdfChangelogManager() {
  const { data: changelog, isLoading } = useAdfChangelog();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [search, setSearch] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<AdfChangelogEntry | null>(null);
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteEntry, setDeleteEntry] = useState<AdfChangelogEntry | null>(null);
  const [localOrder, setLocalOrder] = useState<AdfChangelogEntry[] | null>(null);

  const displayData = localOrder || changelog || [];

  const filteredEntries = useMemo(() => {
    if (!search) return displayData;
    const lower = search.toLowerCase();
    return displayData.filter(entry =>
      entry.version.toLowerCase().includes(lower) ||
      entry.changes.some(c => c.toLowerCase().includes(lower))
    );
  }, [displayData, search]);

  const openCreate = () => {
    setEditingEntry(null);
    setFormData(defaultFormData);
    setIsEditorOpen(true);
  };

  const openEdit = (entry: AdfChangelogEntry) => {
    setEditingEntry(entry);
    setFormData({
      version: entry.version,
      release_date: entry.release_date,
      changes: entry.changes.length ? entry.changes : [''],
      is_current: entry.is_current,
    });
    setIsEditorOpen(true);
  };

  const handleSave = async () => {
    if (!formData.version.trim()) {
      toast({ title: 'Version is required', variant: 'destructive' });
      return;
    }

    const nonEmptyChanges = formData.changes.filter(c => c.trim());
    if (nonEmptyChanges.length === 0) {
      toast({ title: 'At least one change is required', variant: 'destructive' });
      return;
    }

    setIsSaving(true);
    try {
      // If setting as current, unset other current entries
      if (formData.is_current) {
        await supabase
          .from('adf_changelog')
          .update({ is_current: false })
          .neq('id', editingEntry?.id || '');
      }

      const payload = {
        version: formData.version.trim(),
        release_date: formData.release_date,
        changes: nonEmptyChanges,
        is_current: formData.is_current,
      };

      if (editingEntry) {
        const { error } = await supabase
          .from('adf_changelog')
          .update(payload)
          .eq('id', editingEntry.id);
        if (error) throw error;
        toast({ title: 'Changelog entry updated' });
      } else {
        const maxOrder = Math.max(0, ...displayData.map(e => e.display_order));
        const { error } = await supabase
          .from('adf_changelog')
          .insert({ ...payload, display_order: maxOrder + 1 });
        if (error) throw error;
        toast({ title: 'Changelog entry created' });
      }

      queryClient.invalidateQueries({ queryKey: ['adf-changelog'] });
      setIsEditorOpen(false);
      setLocalOrder(null);
    } catch (error: any) {
      toast({ title: 'Error saving entry', description: error.message, variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteEntry) return;
    try {
      const { error } = await supabase
        .from('adf_changelog')
        .delete()
        .eq('id', deleteEntry.id);
      if (error) throw error;
      toast({ title: 'Changelog entry deleted' });
      queryClient.invalidateQueries({ queryKey: ['adf-changelog'] });
      setLocalOrder(null);
    } catch (error: any) {
      toast({ title: 'Error deleting entry', description: error.message, variant: 'destructive' });
    } finally {
      setDeleteEntry(null);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || !changelog) return;
    
    const items = Array.from(displayData);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    
    setLocalOrder(items);

    // Persist new order
    const updates = items.map((item, idx) => ({
      id: item.id,
      display_order: idx + 1,
    }));

    try {
      for (const update of updates) {
        await supabase
          .from('adf_changelog')
          .update({ display_order: update.display_order })
          .eq('id', update.id);
      }
      queryClient.invalidateQueries({ queryKey: ['adf-changelog'] });
    } catch (error: any) {
      toast({ title: 'Error reordering', description: error.message, variant: 'destructive' });
      setLocalOrder(null);
    }
  };

  const addChange = () => {
    setFormData(prev => ({ ...prev, changes: [...prev.changes, ''] }));
  };

  const removeChange = (index: number) => {
    setFormData(prev => ({
      ...prev,
      changes: prev.changes.filter((_, i) => i !== index),
    }));
  };

  const updateChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      changes: prev.changes.map((c, i) => (i === index ? value : c)),
    }));
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <AdminPageHeader
          title="ADF Changelog"
          addLabel="Add Version"
        />
        <AdminTableSkeleton columns={4} rows={3} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="ADF Changelog"
        count={changelog?.length}
        onAdd={openCreate}
        addLabel="Add Version"
      />

      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search versions or changes..."
        className="max-w-sm"
      />

      {filteredEntries.length === 0 ? (
        <AdminEmptyState
          icon={History}
          title="No changelog entries"
          description="Add version history for the ADF specification"
          actionLabel="Add Version"
          onAction={openCreate}
        />
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="changelog">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="rounded-md border"
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Version</TableHead>
                      <TableHead>Release Date</TableHead>
                      <TableHead>Changes</TableHead>
                      <TableHead>Current</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredEntries.map((entry, index) => (
                      <Draggable key={entry.id} draggableId={entry.id} index={index}>
                        {(provided, snapshot) => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={snapshot.isDragging ? 'bg-muted' : ''}
                          >
                            <TableCell>
                              <div
                                {...provided.dragHandleProps}
                                className="cursor-grab text-muted-foreground hover:text-foreground"
                              >
                                <GripVertical className="h-4 w-4" />
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{entry.version}</TableCell>
                            <TableCell>
                              {entry.release_date === '2000-01-01'
                                ? 'Original'
                                : format(new Date(entry.release_date), 'MMM d, yyyy')}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{entry.changes.length} items</Badge>
                            </TableCell>
                            <TableCell>
                              {entry.is_current && (
                                <Check className="h-4 w-4 text-green-600" />
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEdit(entry)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDeleteEntry(entry)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <AdminSheet
        open={isEditorOpen}
        onOpenChange={setIsEditorOpen}
        title={editingEntry ? 'Edit Version' : 'Add Version'}
        description="Manage changelog entry details"
        onSave={handleSave}
        isSaving={isSaving}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="version">Version *</Label>
            <Input
              id="version"
              value={formData.version}
              onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
              placeholder="e.g., v2.0"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="release_date">Release Date</Label>
            <Input
              id="release_date"
              type="date"
              value={formData.release_date}
              onChange={(e) => setFormData(prev => ({ ...prev, release_date: e.target.value }))}
            />
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="is_current"
              checked={formData.is_current}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_current: checked }))}
            />
            <Label htmlFor="is_current">Mark as current version</Label>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Changes *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addChange}>
                <Plus className="h-3 w-3 mr-1" />
                Add
              </Button>
            </div>
            <div className="space-y-2">
              {formData.changes.map((change, index) => (
                <div key={index} className="flex gap-2">
                  <Textarea
                    value={change}
                    onChange={(e) => updateChange(index, e.target.value)}
                    placeholder="Describe a change..."
                    rows={2}
                    className="flex-1"
                  />
                  {formData.changes.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeChange(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </AdminSheet>

      <DeleteConfirmDialog
        open={!!deleteEntry}
        onOpenChange={(open) => !open && setDeleteEntry(null)}
        onConfirm={handleDelete}
        itemName={deleteEntry?.version}
        itemType="changelog entry"
      />
    </div>
  );
}
